import { useBlocker } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'

import { Button } from '@/shared/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog'

type UnsavedChangesGuardProps = {
  /** Usually react-hook-form's `formState.isDirty`. */
  when: boolean
}

/**
 * Stops a navigation away from edits that were never saved, and asks.
 *
 * Nothing else in this application does: a form's dirty state only greys out
 * its own save button, so a click on a dock tile threw the edits away without
 * a word. That is the one interaction where an interruption is worth it —
 * everywhere else a confirmation is noise, but here the alternative is silent
 * data loss with no undo.
 *
 * Navigation inside the application only. `enableBeforeUnload` would also catch
 * closing the tab, but the browser draws that one itself, in its own words and
 * its own chrome, and it cannot be styled or rewritten. A deliberate choice:
 * one foreign dialog is worse than the rarer loss it prevents.
 */
export function UnsavedChangesGuard({ when }: UnsavedChangesGuardProps) {
  const { t } = useTranslation('common')

  const blocker = useBlocker({
    shouldBlockFn: () => when,
    enableBeforeUnload: false,
    withResolver: true,
  })

  return (
    <Dialog
      open={blocker.status === 'blocked'}
      // Dismissing the dialog is a decision too, and it is the safe one: the
      // reader stays where their edits are.
      onOpenChange={(open) => {
        if (!open) blocker.reset?.()
      }}
    >
      <DialogContent className="sm:max-w-modal-confirm">
        <DialogHeader>
          <DialogTitle>{t('unsaved.title')}</DialogTitle>
          <DialogDescription>{t('unsaved.description')}</DialogDescription>
        </DialogHeader>

        <DialogFooter>
          <Button variant="ghost" onClick={() => blocker.reset?.()}>
            {t('unsaved.stay')}
          </Button>
          {/* Leaving is the destructive half here — it is the one that loses
              something — so it wears the destructive tone rather than the
              default, which would read as the recommended way out. */}
          <Button variant="destructive" onClick={() => blocker.proceed?.()}>
            {t('unsaved.leave')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
